/** 操作历史最多保留十五步，另保留一个可撤销的起点。 */
export const EDITOR_HISTORY_LIMIT = 15;
/** 同一输入框连续输入的合并间隔，避免按字符占满历史。 */
const INPUT_MERGE_INTERVAL_MS = 1500;
/** 未进入编辑页面时不记录任何历史。 */
const INITIAL_HISTORY = {
    scope: null,
    entries: [],
    index: 0,
    nextSequence: 1,
    inputKey: null,
    restoreVersion: 0,
};
/** 按项目、章节和节点提取当前节点快照，不复制整份项目文档。 */
export function getEditorHistorySnapshot(document, scope) {
    if (document.project.id !== scope.projectId)
        return null;
    const chapter = document.chapters.find((item) => item.id === scope.chapterId);
    const node = chapter?.nodes.find((item) => item.id === scope.nodeId);
    return chapter && node ? { node, edges: chapter.edges } : null;
}
/** 恢复当前节点和连线，其余节点摘要、项目设置与素材索引保持最新值。 */
export function restoreEditorHistorySnapshot(document, scope, snapshot) {
    if (!getEditorHistorySnapshot(document, scope))
        return document;
    return {
        ...document,
        chapters: document.chapters.map((chapter) => chapter.id !== scope.chapterId
            ? chapter
            : {
                ...chapter,
                edges: snapshot.edges,
                nodes: chapter.nodes.map((node) => (node.id === scope.nodeId ? snapshot.node : node)),
            }),
    };
}
/** 统一消费文档更新，恢复移动游标，新编辑截断后续历史，连续输入合并一步。 */
export function editorHistoryReducer(state = INITIAL_HISTORY, action) {
    if (action.type === 'editorHistory/clear' ||
        action.type === 'projectDocument/loadStarted' ||
        action.type === 'projectDocument/loaded' ||
        action.type === 'projectDocument/cleared')
        return INITIAL_HISTORY;
    if (action.type === 'editorHistory/start') {
        const { scope, snapshot, timestamp } = action.payload;
        if (state.scope?.projectId === scope.projectId &&
            state.scope.chapterId === scope.chapterId &&
            state.scope.nodeId === scope.nodeId)
            return state;
        return {
            ...INITIAL_HISTORY,
            scope,
            entries: [{ sequence: 0, timestamp, snapshot, externalEffects: false }],
        };
    }
    if (action.type !== 'projectDocument/replaced' || !state.scope)
        return state;
    const snapshot = getEditorHistorySnapshot(action.payload, state.scope);
    const current = state.entries.at(state.index);
    if (!snapshot || !current)
        return state;
    if (action.meta.historyIndex !== undefined) {
        if (action.meta.historyIndex < 0)
            return state;
        const target = state.entries.at(action.meta.historyIndex);
        if (!target || JSON.stringify(target.snapshot) !== JSON.stringify(snapshot))
            return state;
        return {
            ...state,
            index: action.meta.historyIndex,
            inputKey: null,
            restoreVersion: state.restoreVersion + 1,
        };
    }
    if (JSON.stringify(current.snapshot) === JSON.stringify(snapshot))
        return state;
    const canMerge = action.meta.inputKey !== undefined &&
        state.inputKey === action.meta.inputKey &&
        state.index > 0 &&
        state.index === state.entries.length - 1 &&
        action.meta.editedAt - current.timestamp <= INPUT_MERGE_INTERVAL_MS;
    const entry = {
        sequence: canMerge ? current.sequence : state.nextSequence,
        timestamp: action.meta.editedAt,
        snapshot,
        externalEffects: Boolean(action.meta.externalEffects) || (canMerge && current.externalEffects),
    };
    const entries = [
        ...state.entries.slice(0, canMerge ? state.index : state.index + 1),
        entry,
    ].slice(-(EDITOR_HISTORY_LIMIT + 1));
    return {
        ...state,
        entries,
        index: entries.length - 1,
        nextSequence: canMerge ? state.nextSequence : state.nextSequence + 1,
        inputKey: action.meta.inputKey ?? null,
    };
}
/** 每个输入控件的临时标识，不进入节点 JSON，也不阻止被卸载的控件回收。 */
const INPUT_KEYS = new WeakMap();
/** 下一个输入控件标识。 */
let nextInputKey = 1;
/** 只对真实输入框的连续编辑合并历史；按钮点击和拖拽保持独立操作。 */
export function getEditorHistoryInputKey() {
    const target = document.activeElement;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) &&
        !(target instanceof HTMLElement && target.isContentEditable))
        return undefined;
    let key = INPUT_KEYS.get(target);
    if (key === undefined) {
        key = nextInputKey;
        nextInputKey += 1;
        INPUT_KEYS.set(target, key);
    }
    return key;
}
